import {
	Badge,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Popover,
	Tooltip
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ChatIcon from "@mui/icons-material/Chat";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { format } from "date-fns";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import useSound from "use-sound";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { getImageUrl } from "../../helpers/imageHelper";
import { gruposCompartilhados } from "../../helpers/groupsVisibility";
import useTickets from "../../hooks/useTickets";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import TicketListItem from "../TicketListItem";

const TabContainer = styled('div')(({ theme }) => ({
	overflowY: "auto",
	maxHeight: 350,
	...(theme.scrollbarStyles || {}),
}));

const PopoverPaper = styled(Popover)(({ theme }) => ({
	'& .MuiPopover-paper': {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	}
}));

const NotificationsPopOver = () => {
	const themeStorage = localStorage.getItem("theme");
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const { user } = useContext(AuthContext);
	// Grupos compartilhados (groupsVisibility = all/queue) não tocam som nem
	// abrem pop-up: com o grupo visível p/ todos, apitaria em todas as máquinas.
	const gruposCompartilhadosRef = useRef(false);
	useEffect(() => {
		api.get("/settings")
			.then(({ data }) => { gruposCompartilhadosRef.current = gruposCompartilhados(data); })
			.catch(() => {});
	}, []);
	const ticketIdUrl = +location.pathname.split("/")[2];
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [isAudioEnabled, setIsAudioEnabled] = useState(
		localStorage.getItem("userAudioEnabled") !== "false"
	);
	// ⚠️ `Notification` NÃO existe no Safari do iOS (só em PWA instalada). Sem esta
	// guarda o acesso solta ReferenceError já na renderização, o ErrorBoundary
	// assume e o painel inteiro vira "Ops! Algo deu errado" no iPhone — o
	// `requestNotificationPermission` abaixo já checava, este useState não.
	// Medido na MC Uniformes: 21 erros entre 07/08 e 11/08/2026, todos iPhone.
	const [notificationsAllowed, setNotificationsAllowed] = useState(
		typeof window !== "undefined" &&
			"Notification" in window &&
			Notification.permission === "granted"
	);
	const { tickets } = useTickets({ withUnreadMessages: "true", all: true });
	// Sem `soundEnabled`: o use-sound congela essa opcao na criacao do player, entao
	// ligar o som pelo botao so passava a valer depois de recarregar a pagina/relogar.
	// A checagem de isAudioEnabled ja e feita no momento de tocar (handleNotifications).
	const [play, { stop }] = useSound(alertSound);
	const soundAlertRef = useRef(play);

	useEffect(() => {
		soundAlertRef.current = play;
	}, [play]);

	// Regra de acesso da notificação (01/08/2026): notifica só o que o usuário
	// realmente acessa, para o sino não tocar com ticket de setor alheio.
	//  - masteradmin: conta técnica, continua vendo tudo
	//  - ticket atribuído a mim: sempre notifica (mesmo em fila que não é minha)
	//  - ticket sem dono: notifica se a fila for minha (ou se o ticket não tiver fila)
	//  - ticket de outro atendente: nunca notifica
	const canNotifyTicket = useCallback(
		(ticket) => {
			if (!ticket) return false;
			if (user?.profile === "masteradmin") return true;
			if (ticket.userId) return ticket.userId === user?.id;
			if (!ticket.queueId) return true;
			return (user?.queues || []).some((queue) => queue.id === ticket.queueId);
		},
		[user]
	);

	useEffect(() => {
		setNotifications(
			(tickets || []).filter(t => t.status !== "closed" && canNotifyTicket(t))
		);
	}, [tickets, canNotifyTicket]);

	const requestNotificationPermission = () => {
		if (!("Notification" in window)) {
			alert(t("notifications.unsupported"));
			return;
		}
		
		Notification.requestPermission()
			.then((permission) => {
				if (permission === "granted") {
					setNotificationsAllowed(true);
					toast.success(t("notifications.permissionGranted"));
				} else if (permission === "denied") {
					toast.error(t("notifications.permissionDenied"));
				}
			})
			.catch((error) => {
				console.error("Erro ao solicitar permissão para notificações:", error);
				toast.error(t("notifications.error"));
			});
	};

	const handleNotifications = useCallback(
		(data) => {

			if (!notificationsAllowed) {
				console.warn("[NOTIFICAÇÃO] Notificações não permitidas pelo navegador");
				// 28/10/2026 (Rio Brás): sem a permissão do navegador não há pop-up,
				// mas o som toca do mesmo jeito — antes o atendente ficava sem aviso nenhum.
				if (isAudioEnabled && soundAlertRef.current) {
					soundAlertRef.current();
				}
				return;
			}

			const { message, contact, ticket } = data;

			const options = {
				body: `${message.body} - ${format(new Date(), "HH:mm")}`,
				icon: contact.profilePicUrl,
				tag: ticket.id,
				renotify: true,
			};

			try {
				const notification = new Notification(
					`${t("tickets.notification.message")} ${contact.name}`,
					options
				);

				notification.onclick = (e) => {
					e.preventDefault();
					window.focus();
					navigate(`/tickets/${ticket.id}`);
				};

			} catch (error) {
				console.error("[NOTIFICAÇÃO] Erro ao exibir notificação:", error);
			}

			if (isAudioEnabled && soundAlertRef.current) {
				soundAlertRef.current();
			}
		},
		[isAudioEnabled, navigate, notificationsAllowed, t]
	);

	useEffect(() => {
		const socket = openSocket();

		const handleConnect = () => {
			socket.emit("joinNotification");
		};
		socket.on("connect", handleConnect);

		const handleAppMessage = (data) => {
			if (!data.ticket) {
				return;
			}

			const hasAccess = canNotifyTicket(data.ticket);

			// Ticket fora do meu acesso: não notifica e ainda tira do contador,
			// caso tenha entrado na lista antes de mudar de fila/dono.
			if (!hasAccess) {
				setNotifications((prevState) =>
					prevState.filter((t) => t.id !== data.ticket.id)
				);
				return;
			}

			if (
				data.action === "create" &&
				!data.message.read
			) {
				if (data.ticket.status === "closed") {
					setNotifications((prevState) => prevState.filter((t) => t.id !== data.ticket.id));
					return;
				}
				setNotifications((prevState) => {
					const ticketIndex = prevState.findIndex((t) => t.id === data.ticket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState.filter(t => t.status !== "closed")];
					}
					return [data.ticket, ...prevState].filter(t => t.status !== "closed");
				});

				const shouldNotNotify = data.message.ticketId === ticketIdUrl && document.visibilityState === "visible";

				if (shouldNotNotify) {
					console.warn("[NOTIFICAÇÃO] Notificação bloqueada - ticket já está aberto na tela");
					return;
				}

				// Silenciar grupos (13/10/2026, pedido do David/DT Barbosa): com a
				// preferência ligada, grupo entra no sino/contador mas sem som e sem
				// pop-up. Liga/desliga pelo sino da aba Grupos.
				if (user?.muteGroups && (data.ticket.isGroup || data.contact?.isGroup)) {
					return;
				}
				if (gruposCompartilhadosRef.current && (data.ticket.isGroup || data.contact?.isGroup)) {
					return;
				}

				handleNotifications(data);
			} else {
				console.warn("[NOTIFICAÇÃO] Condições NÃO atendidas:", {
					isCreate: data.action === "create",
					isUnread: !data.message.read,
					ticketStatus: data.ticket.status,
					ticketQueueId: data.ticket.queueId,
					ticketUserId: data.ticket.userId,
					currentUserId: user?.id
				});
			}
		};
		socket.on("appMessage", handleAppMessage);

		// O componente so ouvia "appMessage", entao ACEITAR ou FINALIZAR um ticket nao
		// tirava o item da lista do sino — ia acumulando notificacao ja tratada.
		// Aqui reagimos tambem ao evento "ticket": sai da lista quando foi fechado,
		// quando deixou de ser meu/da minha fila, ou quando nao ha mais nao lidas.
		const handleTicketChange = (data) => {
			try {
				if (data.action === "delete") {
					setNotifications((prev) => prev.filter((t) => t.id !== data.ticketId));
					return;
				}
				if (data.action !== "update" || !data.ticket) return;
				const t = data.ticket;
				const devesair =
					t.status === "closed" ||
					!canNotifyTicket(t) ||
					Number(t.unreadMessages || 0) === 0;
				setNotifications((prev) =>
					devesair
						? prev.filter((x) => x.id !== t.id)
						: prev.map((x) => (x.id === t.id ? t : x))
				);
			} catch (err) {
				console.error("[NOTIFICAÇÃO] erro ao tratar evento ticket:", err);
			}
		};
		socket.on("ticket", handleTicketChange);

		// Remove só os listeners deste componente; não derruba o socket global.
		return () => {
			socket.off("connect", handleConnect);
			socket.off("appMessage", handleAppMessage);
			socket.off("ticket", handleTicketChange);
		};
	}, [user, ticketIdUrl, handleNotifications, canNotifyTicket]);

	// 28/10/2026 (Rio Brás): mensagem nova no chat interno também toca o som
	// (antes só o contador do menu mudava). Não toca para a própria mensagem nem
	// quando o atendente já está com a tela do chat interno aberta e visível.
	const pathnameRef = useRef(location.pathname);
	useEffect(() => {
		pathnameRef.current = location.pathname;
	}, [location.pathname]);

	useEffect(() => {
		const socket = openSocket();
		if (!socket) return;
		const handleInternalChat = (data) => {
			if (data?.action !== "newMessage" || !data.message) return;
			if (Number(data.message.senderId) === Number(user?.id)) return;
			const naTelaDoChat =
				pathnameRef.current.startsWith("/internal-chat") &&
				document.visibilityState === "visible";
			if (naTelaDoChat) return;
			if (isAudioEnabled && soundAlertRef.current) {
				soundAlertRef.current();
			}
		};
		socket.on("internalChat", handleInternalChat);
		return () => {
			socket.off("internalChat", handleInternalChat);
		};
	}, [user, isAudioEnabled]);

	// Lembrete pessoal (Jds 04/11): chega só para quem marcou, na sala do
	// usuário. Toca o som mesmo com o som de mensagens desligado: foi a pessoa
	// que pediu para ser lembrada.
	useEffect(() => {
		const socket = openSocket();
		if (!socket) return;
		const handleLembrete = (data) => {
			const l = data && data.lembrete;
			if (!l || data.action !== "aviso" || Number(l.userId) !== Number(user?.id)) return;
			const abrir = () => { window.focus(); navigate(`/tickets/${l.ticketId}`); };
			const titulo = `⏰ Lembrete${l.contato ? ` · ${l.contato}` : ""}`;
			toast.info(
				<div onClick={abrir} style={{ cursor: "pointer" }}>
					<b>{titulo}</b>
					<div style={{ whiteSpace: "pre-wrap" }}>{l.comentario}</div>
					{l.mensagem && <div style={{ opacity: 0.75, fontStyle: "italic", marginTop: 4 }}>{l.mensagem.slice(0, 140)}</div>}
					<div style={{ fontSize: 12, marginTop: 4, textDecoration: "underline" }}>Abrir a conversa</div>
				</div>,
				{ autoClose: false, toastId: `lembrete-${l.id}` }
			);
			try {
				if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
					const n = new Notification(titulo, { body: `${l.comentario}${l.mensagem ? `\n${l.mensagem.slice(0, 120)}` : ""}`, tag: `lembrete-${l.id}` });
					n.onclick = (e) => { e.preventDefault(); abrir(); };
				}
			} catch (e) {}
			if (soundAlertRef.current) soundAlertRef.current();
		};
		socket.on("lembrete", handleLembrete);
		return () => { socket.off("lembrete", handleLembrete); };
	}, [user, navigate]);

	useEffect(() => {
		const updateDocumentTitle = async () => {
			try {
				const { data } = await api.get("/personalizations");
				let baseTitle = "SabeZap Ticket";
				let faviconUrl =
					"/assets/favicon.ico?v=2";
				if (data && data.length > 0) {
					const lightConfig = data.find((config) => config.theme === "light");
					const darkConfig = data.find((config) => config.theme === "dark");

					if (lightConfig && lightConfig.company) {
						baseTitle = "SZT - " + lightConfig.company;
					}
					if (themeStorage === "dark" && darkConfig) {
						faviconUrl = darkConfig.favico || faviconUrl;
					} else if (lightConfig) {
						faviconUrl = lightConfig.favico || faviconUrl;
					}
				}
				document.title =
					notifications.length > 0
						? `(${notifications.length}) ${baseTitle}`
						: baseTitle;

				let link = document.querySelector("link[rel~='icon']");
				if (!link) {
					link = document.createElement("link");
					link.rel = "icon";
					document.getElementsByTagName("head")[0].appendChild(link);
				}
				link.href = getImageUrl(faviconUrl);
			} catch (err) {
				toastError(err, t);
				document.title = "Erro ao carregar título";
			}
		};

		updateDocumentTitle();
	}, [notifications, themeStorage, t]);

	const toggleAudio = () => {
		const newAudioStatus = !isAudioEnabled;
		setIsAudioEnabled(newAudioStatus);
		localStorage.setItem("userAudioEnabled", newAudioStatus.toString());

		if (!newAudioStatus) {
			stop();
		}
	};

	const handleClick = () => {
		setIsOpen((prevState) => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const NotificationTicket = ({ children }) => (
		<div onClick={handleClickAway}>{children}</div>
	);

	return (
		<>
			<IconButton onClick={toggleAudio} aria-label="Toggle Sound" color="inherit">
				{isAudioEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
			</IconButton>
			{!notificationsAllowed && (
				<IconButton
					onClick={requestNotificationPermission}
					color="inherit"
					variant="contained"
				>
					<Tooltip title={t("notifications.allow")} arrow>
						<HelpOutlineIcon />
					</Tooltip>
				</IconButton>
			)}
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				color="inherit"
			>
				<Badge
					badgeContent={notifications.length}
					color="secondary"
					overlap="rectangular"
					max={999999}
				>
					<ChatIcon />
				</Badge>
			</IconButton>
			<PopoverPaper
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				onClose={handleClickAway}
			>
				<List dense component={TabContainer}>
					{notifications.length === 0 ? (
						<ListItem>
							<ListItemText>{t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map((ticket) => (
							<NotificationTicket key={ticket.id}>
								<TicketListItem ticket={ticket} />
							</NotificationTicket>
						))
					)}
				</List>
			</PopoverPaper>
		</>
	);
};

export default NotificationsPopOver;
